<template>
  <div class="relative rounded mb-4">
    <!-- Email Modal -->
    <email-modal
      :show="showEmailModal"
      @update:show="showEmailModal = $event"
      @submit="handleEmailSubmit"
      @cancel="handleEmailCancel"
      title="User Information Required"
      description="Please provide your information to calculate trip costs."
      calculation="route_cost_report"
    />

    <!-- Direction Form - always visible -->
    <div class="transition-all duration-300 ease-in-out">
      <direction-form ref="directionForm" @route-updated="updateRouteValidity"></direction-form>
    </div>

    <!-- Toggle Button - only visible when valid route exists and trip cost panel is closed -->
    <div v-if="hasValidRoute && !showTripCostPanel" class="flex justify-center my-4">
      <button
        @click="toggleTripCostPanel"
        class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md shadow-md transition-colors duration-200 flex items-center gap-x-2"
      >
        <svg class="w-4 h-4 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"/>
        </svg>
        <span>Calculate Trip Cost</span>
      </button>
    </div>

    <!-- Trip Cost Panel - appears below direction form when shown -->
    <div
      v-show="showTripCostPanel"
      class="transition-all duration-300 ease-in-out mt-4"
      :class="showTripCostPanel ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform -translate-y-4'"
    >
      <trip-cost-calculator @close-calculator="showDirectionForm"></trip-cost-calculator>
    </div>
  </div>
</template>

<script>
import TripCostCalculator from './TripCostCalculator.vue';
import DirectionForm from './DirectionForm.vue';
import EmailModal from '../common/EmailModal.vue';
import { mapState } from 'vuex';

export default {
  name: 'GoogleMapDirectionsContainer',
  components: {
    TripCostCalculator,
    DirectionForm,
    EmailModal
  },
  props: {
    requireEmail: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showTripCostPanel: false,
      routeIsValid: false,
      showEmailModal: false,
      userEmail: ''
    };
  },
  computed: {
    ...mapState(['reatlas']),

    hasValidRoute() {
      // Use the data property which will be updated by the child component
      return this.routeIsValid;
    }
  },
  methods: {
    toggleTripCostPanel() {
      // Only check for email if requireEmail prop is true (public view)
      if (this.requireEmail && !this.userEmail) {
        // Show email modal first
        this.showEmailModal = true;
        return;
      }
      
      // Email not required or already exists, show the panel
      this.showTripCostPanel = !this.showTripCostPanel;
    },
    
    handleEmailSubmit(userInfo) {
      // Store user info (already handled by EmailModal component)
      this.userEmail = userInfo.email;
      
      // Hide modal
      this.showEmailModal = false;

      // Emit event to update header with user info
      this.$root.$emit('email-submitted', userInfo);

      // Now show the trip cost panel
      this.showTripCostPanel = true;
    },

    handleEmailCancel() {
      this.showEmailModal = false;
    },
    
    showDirectionForm() {
      this.showTripCostPanel = false;
    },
    updateRouteValidity(event) {
      // Update route validity based on event from DirectionForm
      if (event && typeof event.valid === 'boolean') {
        this.routeIsValid = event.valid;
        console.log('Route validity updated from event:', this.routeIsValid);
      } else {
        // Fallback: Check if direction form has a valid route
        if (this.$refs.directionForm) {
          this.routeIsValid = this.$refs.directionForm.directionsSet && !this.$refs.directionForm.errorMessage;
          console.log('Route validity updated from ref:', this.routeIsValid);
        }
      }
    }
  },
  mounted() {
    // Only check for stored email if requireEmail prop is true (public view)
    if (this.requireEmail) {
      const storedEmail = sessionStorage.getItem('public_view_email');
      if (storedEmail) {
        this.userEmail = storedEmail;
      }
    }
    
    // Watch for changes in the direction form
    this.$watch(
      () => this.$refs.directionForm?.directionsSet,
      () => {
        this.updateRouteValidity();
      }
    );
    this.$watch(
      () => this.$refs.directionForm?.errorMessage,
      () => {
        this.updateRouteValidity();
      }
    );
  }
};
</script>

<style scoped>
/* Add smooth transitions for the panel */
.transition-all {
  transition: all 0.3s ease-in-out;
}

/* Ensure the trip cost panel appears above everything */
.z-20 {
  z-index: 20;
}

/* Container positioning for absolute overlay */
.relative {
  position: relative;
}

.absolute {
  position: absolute;
}

.inset-0 {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}
</style>
